import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@baluu/ui/components/card";
import { Input } from "@baluu/ui/components/input";
import { Label } from "@baluu/ui/components/label";
import { Button } from "@baluu/ui/components/button";
import { TabSkeleton, TabError } from "../TabStatus";
import type { ProfileData } from "../types";

/**
 * Ported from ProfileView.tsx's `SettingsForm` (:873-945). Adds a `phone`
 * field — the original only edited firstName/lastName/email — because
 * `/widget/me/profile`'s PUT accepts `{firstName?, lastName?, phone?}`
 * (../useProfileData.ts:133-141) and `ProfileData` carries a `phone` field
 * that the hosted form never surfaced. Email stays read-only, same as the
 * original ("Email cannot be changed").
 */
export function SettingsTab({
  profile,
  isLoading,
  isError,
  onRetry,
  onSave,
  isSaving,
  saveError,
}: {
  profile?: ProfileData;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSave: (patch: { firstName?: string; lastName?: string; phone?: string }) => void;
  isSaving: boolean;
  saveError: boolean;
}) {
  const [loadedProfileId, setLoadedProfileId] = useState<string | undefined>(undefined);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");

  // Sync local form state once the profile query resolves (this tab mounts
  // before it does, so the initial `useState("")` above can't see it yet) —
  // done during render rather than in a useEffect, per React's documented
  // pattern for adjusting state when a prop changes:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
  // Keyed on `profile.id` (stable across a post-save refetch) so it seeds
  // the fields once without clobbering in-progress edits or a value the
  // customer just saved.
  if (profile && profile.id !== loadedProfileId) {
    setLoadedProfileId(profile.id);
    setFirstName(profile.firstName ?? "");
    setLastName(profile.lastName ?? "");
    setPhone(profile.phone ?? "");
  }

  if (isLoading) return <TabSkeleton rows={3} />;
  if (isError) return <TabError onRetry={onRetry} />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-w-md space-y-4">
          <div>
            <Label htmlFor="profile-first-name" className="mb-2 block">
              First Name
            </Label>
            <Input id="profile-first-name" name="firstName" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="profile-last-name" className="mb-2 block">
              Last Name
            </Label>
            <Input id="profile-last-name" name="lastName" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="profile-phone" className="mb-2 block">
              Phone
            </Label>
            <Input id="profile-phone" name="phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="profile-email" className="mb-2 block">
              Email
            </Label>
            <Input id="profile-email" name="email" value={profile?.email ?? ""} disabled />
            <p className="mt-1 text-xs text-muted-foreground">Email cannot be changed</p>
          </div>
          {saveError && <p className="text-sm text-destructive">Couldn&apos;t save changes. Please try again.</p>}
          <Button onClick={() => onSave({ firstName, lastName, phone })} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
